import React from 'react';
import Icon from '@/components/ui/AppIcon';
import { SectionLayoutConfig } from '@/features/projects/types';
import { FooterLayoutId, SOCIAL_PLATFORMS } from '@/constants/layoutTypes';

interface FooterSectionProps {
    productName: string;
    primaryColor: string;
    logoUrl?: string | null;
    layout?: FooterLayoutId;
    layoutConfig?: SectionLayoutConfig;
    navbarLayoutConfig?: SectionLayoutConfig; // to read logo display prefs
}

// Social icon SVGs (inline for reliability — no heroicons for brand logos)
const SOCIAL_SVGS: Record<string, React.ReactNode> = {
    twitter: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>
    ),
    linkedin: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>
    ),
    github: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.113.82-.258.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.809 1.305 3.495.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12" /></svg>
    ),
    instagram: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z" /></svg>
    ),
    facebook: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>
    ),
    youtube: (
        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814z" /><path fill="#fff" d="M9.545 15.568V8.432L15.818 12z" /></svg>
    ),
};

function SocialIcon({ platform, url }: { platform: string; url: string }) {
    const svg = SOCIAL_SVGS[platform];
    if (!svg) return null;
    return (
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white transition-colors">
            {svg}
        </a>
    );
}

function SocialIconDark({ platform, url }: { platform: string; url: string }) {
    const svg = SOCIAL_SVGS[platform];
    if (!svg) return null;
    return (
        <a href={url} target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-gray-600 transition-colors">
            {svg}
        </a>
    );
}

// ── Layout 1: Simple ───────────────────────────────────────────────────────

function FooterSimple({ productName, primaryColor, logoUrl, layoutConfig, navbarLayoutConfig }: FooterSectionProps) {
    const bgColor = layoutConfig?.footerBgColor || primaryColor;
    const socialLinks = layoutConfig?.socialLinks || {};
    const hasSocials = Object.values(socialLinks).some(v => v);
    const logoH = navbarLayoutConfig?.logoHeight || 24;
    const showText = navbarLayoutConfig?.showLogoText !== false;
    const displayText = navbarLayoutConfig?.logoText || productName;

    return (
        <footer className="py-10 px-6 text-center" style={{ backgroundColor: bgColor }}>
            <div className="max-w-6xl mx-auto">
                <div className="flex items-center justify-center gap-2 mb-3">
                    {logoUrl && <img src={logoUrl} alt={productName} className="w-auto object-contain" style={{ height: `${logoH}px` }} />}
                    {showText && <p className="font-semibold text-white">{displayText}</p>}
                </div>
                {hasSocials && (
                    <div className="flex items-center justify-center gap-4 mb-4">
                        {SOCIAL_PLATFORMS.map(p => socialLinks[p.key] ? <SocialIcon key={p.key} platform={p.key} url={socialLinks[p.key]!} /> : null)}
                    </div>
                )}
                <p className="text-white/50 text-xs">© {new Date().getFullYear()} {displayText}. All rights reserved.</p>
            </div>
        </footer>
    );
}

// ── Layout 2: Columns ──────────────────────────────────────────────────────

function FooterColumns({ productName, primaryColor, logoUrl, layoutConfig, navbarLayoutConfig }: FooterSectionProps) {
    const bgColor = layoutConfig?.footerBgColor || primaryColor;
    const socialLinks = layoutConfig?.socialLinks || {};
    const footerLinks = layoutConfig?.footerLinks || [];
    const hasSocials = Object.values(socialLinks).some(v => v);
    const logoH = navbarLayoutConfig?.logoHeight || 24;
    const showText = navbarLayoutConfig?.showLogoText !== false;
    const displayText = navbarLayoutConfig?.logoText || productName;

    return (
        <footer className="py-14 px-6" style={{ backgroundColor: bgColor }}>
            <div className="max-w-6xl mx-auto">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-10 mb-10">
                    {/* Brand Column */}
                    <div>
                        <div className="flex items-center gap-2 mb-3">
                            {logoUrl && <img src={logoUrl} alt={productName} className="w-auto object-contain" style={{ height: `${logoH}px` }} />}
                            {showText && <p className="font-bold text-white text-lg">{displayText}</p>}
                        </div>
                        <p className="text-white/60 text-sm leading-relaxed">
                            Building something great, one step at a time.
                        </p>
                    </div>

                    {/* Links Column */}
                    {footerLinks.length > 0 && (
                        <div>
                            <p className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Links</p>
                            <div className="flex flex-col gap-2.5">
                                {footerLinks.map((link, i) => (
                                    <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="text-white/60 hover:text-white text-sm transition-colors">
                                        {link.label}
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Social Column */}
                    {hasSocials && (
                        <div>
                            <p className="font-semibold text-white mb-4 text-sm uppercase tracking-wider">Connect</p>
                            <div className="flex items-center gap-3">
                                {SOCIAL_PLATFORMS.map(p => socialLinks[p.key] ? <SocialIcon key={p.key} platform={p.key} url={socialLinks[p.key]!} /> : null)}
                            </div>
                        </div>
                    )}
                </div>

                <div className="border-t border-white/10 pt-6">
                    <p className="text-white/40 text-xs text-center">© {new Date().getFullYear()} {displayText}. All rights reserved.</p>
                </div>
            </div>
        </footer>
    );
}

// ── Layout 3: Centered Rich ────────────────────────────────────────────────

function FooterCenteredRich({ productName, primaryColor, logoUrl, layoutConfig, navbarLayoutConfig }: FooterSectionProps) {
    const bgColor = layoutConfig?.footerBgColor || primaryColor;
    const socialLinks = layoutConfig?.socialLinks || {};
    const footerLinks = layoutConfig?.footerLinks || [];
    const hasSocials = Object.values(socialLinks).some(v => v);
    const logoH = navbarLayoutConfig?.logoHeight || 28;
    const showText = navbarLayoutConfig?.showLogoText !== false;
    const displayText = navbarLayoutConfig?.logoText || productName;

    return (
        <footer className="py-14 px-6 text-center" style={{ backgroundColor: bgColor }}>
            <div className="max-w-4xl mx-auto">
                {/* Logo */}
                <div className="flex items-center justify-center gap-2 mb-6">
                    {logoUrl && <img src={logoUrl} alt={productName} className="w-auto object-contain" style={{ height: `${logoH}px` }} />}
                    {showText && <p className="font-bold text-white text-xl">{displayText}</p>}
                </div>

                {/* Nav links row */}
                {footerLinks.length > 0 && (
                    <div className="flex flex-wrap items-center justify-center gap-6 mb-6">
                        {footerLinks.map((link, i) => (
                            <a key={i} href={link.url} target="_blank" rel="noopener noreferrer" className="text-white/70 hover:text-white text-sm font-medium transition-colors">
                                {link.label}
                            </a>
                        ))}
                    </div>
                )}

                {/* Social icons */}
                {hasSocials && (
                    <div className="flex items-center justify-center gap-4 mb-6">
                        {SOCIAL_PLATFORMS.map(p => socialLinks[p.key] ? <SocialIcon key={p.key} platform={p.key} url={socialLinks[p.key]!} /> : null)}
                    </div>
                )}

                <div className="border-t border-white/10 pt-6">
                    <p className="text-white/40 text-xs">© {new Date().getFullYear()} {displayText}. All rights reserved.</p>
                </div>
            </div>
        </footer>
    );
}

// ── Main Export ─────────────────────────────────────────────────────────────

export function FooterSection(props: FooterSectionProps) {
    const { layout = 'simple' } = props;

    switch (layout) {
        case 'columns':
            return <FooterColumns {...props} />;
        case 'centered-rich':
            return <FooterCenteredRich {...props} />;
        case 'simple':
        default:
            return <FooterSimple {...props} />;
    }
}
